import { DatePipe } from '@angular/common';
import { booleanAttribute, Component, computed, input, output } from '@angular/core';
import dayjs from 'dayjs';
import { UmssBadgeComponent } from '../badge/badge.component';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssSwitchComponent } from '../switch/switch.component';

/**
 * One subscription product: heading, active state and description, with the
 * product-specific body projected in.
 *
 * The toggle is presentational — it reports intent and never commits. The page
 * confirms via a dialog and drives `checked` back to the committed value, so a
 * cancelled toggle visually reverts.
 */
@Component({
  selector: 'umss-subscription-product-card',
  standalone: true,
  imports: [DatePipe, UmssBadgeComponent, UmssIconComponent, UmssSwitchComponent],
  templateUrl: './subscription-product-card.component.html',
  styleUrl: './subscription-product-card.component.scss'
})
export class UmssSubscriptionProductCardComponent {
  title = input.required<string>();
  description = input.required<string>();

  /** Displayed toggle state — driven by the page, not by this component. */
  checked = input.required<boolean>();

  disabled = input(false, { transform: booleanAttribute });

  /**
   * Shows the state as a badge instead of a switch. Used where the viewer can
   * see the subscription but does not control it — a facility whose billing is
   * managed by its parent corporation.
   */
  readonly_ = input(false, { alias: 'readonly', transform: booleanAttribute });

  /**
   * The last date a product the facility has switched off still works, as
   * `YYYY-MM-DD`. Null while nothing is ending.
   *
   * Removal takes effect at the cycle boundary, so between the two the product
   * is switched off but still working. Naming that window matters: calling it
   * "inactive" would be wrong, and a facility that believed it would stop using
   * something it has already paid for.
   *
   * A date rather than an instant because this component is shared and has no
   * facility context — it cannot know the facility's timezone, so it must not
   * be asked to derive a date from one. The API resolves it in that zone
   * instead, which is also what makes it agree with the backend's own cutoff.
   */
  lastCoveredDate = input<string | null>(null);

  /** Fires with the requested state when the admin flips the toggle. */
  activeChange = output<boolean>();

  /** True while the product is switched off but has not stopped yet. */
  protected readonly ending = computed(
    () => !this.checked() && !!this.lastCoveredDate()
  );

  /** The last day the product can still be used, for display. */
  protected readonly lastActiveOn = computed(() => {
    const lastCovered = this.lastCoveredDate();
    return lastCovered ? dayjs(lastCovered).startOf('day').toDate() : null;
  });

  protected readonly statusLabel = computed(() =>
    this.checked() ? 'Subscription Active' : 'Subscription inactive'
  );
}
